import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { ApiError } from "@/api/client";
import { useCountries } from "@/api/hooks";
import { useCompany, useSaveCompany } from "@/api/sales";
import type { CompanyProfile, Weekday } from "@/api/types";
import { LocationFields, findCountry } from "@/components/crm/LocationFields";
import { Alert, PageHeader, Spinner, TextField } from "@/components/ui";
import { allZones } from "@/lib/time";

const TEXT = ["name", "legal_name", "tax_id", "address_line", "city", "state", "postal_code", "country", "phone", "email", "website", "quote_prefix", "quote_terms"] as const;
type Values = Record<(typeof TEXT)[number], string> & { quote_validity_days: string; currency: string; timezone: string };
type Rate = { state: string; rate: string };
const WEEKDAYS: [Weekday, string][] = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];
const DEFAULT_HOURS = Object.fromEntries(WEEKDAYS.map(([d]) => [d, { open: d !== "sun", from: d === "sat" ? "10:00" : "09:30", to: d === "sat" ? "14:00" : "18:00" }])) as Record<Weekday, { open: boolean; from: string; to: string }>;

export function CompanyPage() {
  const company = useCompany();
  const save = useSaveCompany();
  const countries = useCountries();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ tone: "ok" | "danger"; text: string } | null>(null);
  const [rates, setRates] = useState<Rate[]>([]);
  const [aiOn, setAiOn] = useState(true);
  const [warranty, setWarranty] = useState("12");
  const [sla, setSla] = useState<Record<string, string>>({ urgent: "4", high: "24", normal: "72", low: "120" });
  const [hoursOn, setHoursOn] = useState(false);
  const [hours, setHours] = useState<Record<Weekday, { open: boolean; from: string; to: string }>>(DEFAULT_HOURS);
  const [holidays, setHolidays] = useState("");
  const form = useForm<Values>();
  const { register, handleSubmit, reset, watch } = form;
  useEffect(() => {
    if (!company.data) return;
    reset({ ...Object.fromEntries(TEXT.map((k) => [k, (company.data as CompanyProfile)[k] ?? ""])), quote_validity_days: String(company.data.quote_validity_days), currency: company.data.currency, timezone: company.data.timezone } as Values);
    setRates(Object.entries(company.data.us_sales_tax_rates ?? {}).map(([state, rate]) => ({ state, rate })));
    setAiOn(company.data.ai_enabled ?? true);
    const svc = company.data.service_settings ?? {};
    setWarranty(String(svc.warranty_months ?? 12));
    setSla((cur) => ({ ...cur, ...Object.fromEntries(Object.entries(svc.sla_hours ?? {}).map(([k, v]) => [k, String(v)])) }));
    const bh = company.data.business_hours;
    setHoursOn(!!bh);
    if (bh) {
      setHours(Object.fromEntries(WEEKDAYS.map(([d]) => {
        const h = bh.days[d];
        return [d, h ? { open: true, from: h[0], to: h[1] } : { ...DEFAULT_HOURS[d], open: false }];
      })) as Record<Weekday, { open: boolean; from: string; to: string }>);
      setHolidays(bh.holidays.join(", "));
    }
  }, [company.data, reset]);

  const country = findCountry(countries.data, watch("country"));
  const usStates = findCountry(countries.data, "US")?.states ?? [];

  const onSubmit = handleSubmit(async (v) => {
    setErrors({}); setMessage(null);
    const body: Record<string, unknown> = {
      quote_validity_days: Number(v.quote_validity_days), currency: v.currency, timezone: v.timezone, ai_enabled: aiOn,
      service_settings: { warranty_months: Number(warranty) || 0, sla_hours: Object.fromEntries(Object.entries(sla).filter(([, h]) => Number(h) > 0).map(([k, h]) => [k, Number(h)])) },
    };
    for (const k of TEXT) body[k] = v[k].trim() === "" ? null : v[k].trim();
    body.business_hours = hoursOn ? {
      days: Object.fromEntries(WEEKDAYS.map(([d]) => [d, hours[d].open ? [hours[d].from, hours[d].to] : null])),
      holidays: holidays.split(/[\s,]+/).filter(Boolean),
    } : null;
    if (country?.code === "US") body.us_sales_tax_rates = Object.fromEntries(rates.filter((r) => r.state && r.rate !== "").map((r) => [r.state, r.rate]));
    try { await save.mutateAsync(body); setMessage({ tone: "ok", text: "Company profile saved. New quotes will use these details." }); }
    catch (e) { if (e instanceof ApiError && e.problem.errors?.length) setErrors(e.fieldErrors()); else setMessage({ tone: "danger", text: e instanceof Error ? e.message : "Could not save" }); }
  });

  if (company.isLoading) return <Spinner />;
  return (
    <>
      <PageHeader title="Company profile" subtitle="Printed on every quote. Your country and state decide which tax rules apply." />
      <form onSubmit={onSubmit} className="space-y-6" noValidate>
        {message && <Alert tone={message.tone}>{message.text}</Alert>}
        <section className="card grid gap-4 p-5 sm:grid-cols-2" aria-label="Company">
          <TextField label="Display name" {...register("name")} error={errors.name} />
          <TextField label="Legal name" {...register("legal_name")} error={errors.legal_name} />
          <TextField label="Phone" {...register("phone")} error={errors.phone} />
          <TextField label="Email" type="email" {...register("email")} error={errors.email} />
          <TextField label="Website" {...register("website")} error={errors.website} />
          <TextField label="City" {...register("city")} error={errors.city} />
          <div className="sm:col-span-2"><TextField label="Address" {...register("address_line")} error={errors.address_line} /></div>
          <TextField label="PIN / Postal code" {...register("postal_code")} error={errors.postal_code} />
          <LocationFields form={form} errors={errors} taxHint={country?.code === "IN" ? "Your state decides CGST and SGST or IGST" : undefined} />
        </section>

        {country && <TaxSummary code={country.code} name={country.name} taxName={country.tax_name} rate={country.standard_rate} region={country.region} />}

        {country?.code === "US" && (
          <section className="card space-y-3 p-5" aria-label="Sales tax nexus">
            <div>
              <h2 className="font-semibold">Sales tax by state</h2>
              <p className="text-sm text-muted">List only the states where you must collect sales tax (where you have nexus). Sales to other states carry no tax and a use-tax note.</p>
            </div>
            {errors.us_sales_tax_rates && <Alert>{errors.us_sales_tax_rates}</Alert>}
            {rates.map((r, i) => (
              <div key={i} className="flex items-end gap-2">
                <div className="flex-1">
                  <label htmlFor={`nexus-state-${i}`} className="label">State</label>
                  <select id={`nexus-state-${i}`} className="input" value={r.state} onChange={(e) => setRates(rates.map((x, j) => j === i ? { ...x, state: e.target.value } : x))}>
                    <option value="">Choose…</option>
                    {usStates.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
                  </select>
                </div>
                <div className="w-36"><TextField id={`nexus-rate-${i}`} label="Rate %" inputMode="decimal" value={r.rate} onChange={(e) => setRates(rates.map((x, j) => j === i ? { ...x, rate: e.target.value } : x))} /></div>
                <button type="button" className="btn-ghost" onClick={() => setRates(rates.filter((_, j) => j !== i))} aria-label={`Remove ${r.state || "row"}`}>Remove</button>
              </div>
            ))}
            <button type="button" className="btn-ghost" onClick={() => setRates([...rates, { state: "", rate: "" }])}>Add a state</button>
          </section>
        )}

        <section className="card grid gap-4 p-5 sm:grid-cols-2" aria-label="Quote defaults">
          <TextField label="Quote number prefix" hint="e.g. Q gives Q-2026-0001" {...register("quote_prefix")} error={errors.quote_prefix} />
          <TextField label="Quote validity (days)" type="number" min={1} max={365} {...register("quote_validity_days")} error={errors.quote_validity_days} />
          <TextField label="Default currency" maxLength={3} {...register("currency")} error={errors.currency} />
          <div>
            <label htmlFor="timezone" className="label">Company time zone</label>
            <select id="timezone" className="input" {...register("timezone")}>
              {allZones().map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
            </select>
            <p className="mt-1 text-xs text-muted">Quote dates and validity end at midnight here. People still see times in their own zone.</p>
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="quote_terms" className="label">Standard terms and conditions</label>
            <textarea id="quote_terms" className="input min-h-28" {...register("quote_terms")} placeholder={"1. Prices ex-works.\n2. Freight and insurance extra at actuals."} />
          </div>
        </section>
        <section className="card grid gap-4 p-5 sm:grid-cols-2" aria-label="Service">
          <h2 className="font-semibold sm:col-span-2">After-sales service</h2>
          <div><label htmlFor="warranty_months" className="label">Standard warranty (months)</label>
            <input id="warranty_months" className="input" type="number" min={0} max={120} value={warranty} onChange={(e) => setWarranty(e.target.value)} />
            <p className="mt-1 text-xs text-muted">Used when a machine is added to the installed base.</p></div>
          <fieldset><legend className="label">Fix a ticket within (hours)</legend>
            <div className="grid grid-cols-4 gap-2">
              {(["urgent", "high", "normal", "low"] as const).map((p) => (
                <div key={p}><label htmlFor={`sla-${p}`} className="text-xs capitalize text-muted">{p}</label>
                  <input id={`sla-${p}`} className="input" type="number" min={1} value={sla[p] ?? ""} onChange={(e) => setSla({ ...sla, [p]: e.target.value })} /></div>
              ))}
            </div></fieldset>
        </section>
        <section className="card space-y-3 p-5" aria-label="Working hours">
          <div><h2 className="font-semibold">Working hours</h2>
            <p className="text-sm text-muted">Reply targets in the inbox count only working time, in the company's time zone. A message on Friday evening is due on Monday morning.</p></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4 accent-brand" checked={hoursOn} onChange={(e) => setHoursOn(e.target.checked)} />Count reply targets in working hours only</label>
          {hoursOn && <>
            <div className="grid gap-2 xl:grid-cols-2">
              {WEEKDAYS.map(([d, label]) => (
                <div key={d} className="flex items-center gap-2 text-sm">
                  <label className="flex w-28 items-center gap-2"><input type="checkbox" className="h-4 w-4 accent-brand" checked={hours[d].open} onChange={(e) => setHours({ ...hours, [d]: { ...hours[d], open: e.target.checked } })} />{label}</label>
                  <input type="time" aria-label={`${label} opens`} className="input w-36" disabled={!hours[d].open} value={hours[d].from} onChange={(e) => setHours({ ...hours, [d]: { ...hours[d], from: e.target.value } })} />
                  <span className="text-muted">to</span>
                  <input type="time" aria-label={`${label} closes`} className="input w-36" disabled={!hours[d].open} value={hours[d].to} onChange={(e) => setHours({ ...hours, [d]: { ...hours[d], to: e.target.value } })} />
                </div>
              ))}
            </div>
            <TextField id="holidays" label="Holidays" hint="Dates as YYYY-MM-DD, separated by commas" value={holidays} onChange={(e) => setHolidays(e.target.value)} error={errors["business_hours.holidays.0"] ?? errors["business_hours.days"]} />
          </>}
        </section>
        <section className="card space-y-2 p-5" aria-label="AI">
          <h2 className="font-semibold">AI assistant</h2>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="accent-brand" checked={aiOn} onChange={(e) => setAiOn(e.target.checked)} />
            Allow AI summaries, drafts and questions for this workspace</label>
          <p className="text-xs text-muted">Only the record you ask about is sent to the AI provider. Nothing is sent or changed without a person. Your data is not used to train models.</p>
        </section>
        <div className="flex justify-end"><button type="submit" className="btn-primary" disabled={save.isPending}>Save profile</button></div>
      </form>
    </>
  );
}

const RULES: Record<string, string> = {
  IN: "Same state: CGST + SGST. Other state: IGST. Outside India: export under LUT, no GST.",
  CA: "GST, HST or GST + PST/QST by the buyer's province. Outside Canada: zero-rated.",
  US: "Sales tax only for states you list below, at your rate. Outside the US: no sales tax.",
  AU: "GST on Australian sales. Exports are GST-free.",
};

function TaxSummary({ code, name, taxName, rate, region }: { code: string; name: string; taxName: string; rate: string; region: string | null }) {
  const rule = RULES[code]
    ?? (region === "EU" ? `${taxName} on sales in ${name} and to EU consumers. EU businesses with a valid VAT number: reverse charge. Outside the EU: exempt export.`
      : region === "GCC" ? `${taxName} on sales in ${name}. Other countries, including GCC members: zero-rated export.`
        : `${taxName} on sales in ${name}. Exports are zero-rated.`);
  return (
    <section className="card flex flex-wrap items-start gap-x-6 gap-y-2 p-5" aria-label="Tax rules">
      <div><p className="text-xs uppercase tracking-wide text-muted">Tax rules for</p><p className="font-semibold">{name}</p></div>
      <div><p className="text-xs uppercase tracking-wide text-muted">Standard {taxName} rate</p><p className="font-semibold">{Number(rate)}%</p></div>
      <p className="min-w-60 flex-1 text-sm text-muted">{rule} New products default to the standard rate.</p>
    </section>
  );
}
